"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import {
  ScanLine,
  Building2,
  Upload,
  Brain,
  FileCheck,
  Scale,
  ArrowRight,
  ChevronRight,
  Home,
  Briefcase,
  Landmark,
  Globe,
  Search,
  FileWarning,
  ClipboardList,
  ShieldCheck,
  Handshake,
  Shield,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  MessageSquare,
  FileText,
  BookOpen,
  ListChecks,
  BarChart3,
  ScrollText,
  CreditCard,
  Sparkles,
  Check,
  Circle,
  Send,
  Zap,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";

import { SiteHeader } from "@/components/nav/site-header";

const DOC_KEYS = [
  "heroDoc1", "heroDoc2", "heroDoc3", "heroDoc4", "heroDoc5",
  "heroDoc6", "heroDoc7", "heroDoc8", "heroDoc9", "heroDoc10",
];

const LANG_CYCLE = [
  "आपकी भाषा में.",    // Hindi
  "உங்கள் மொழியில்.",   // Tamil
  "మీ భాషలో.",          // Telugu
  "ನಿಮ್ಮ ಭಾಷೆಯಲ್ಲಿ.",    // Kannada
  "നിങ്ങളുടെ ഭാഷയിൽ.", // Malayalam
  "তোমার ভাষায়.",       // Bengali
  "तुमच्या भाषेत.",      // Marathi
  "તમારી ભાષામાં.",     // Gujarati
  "ਤੁਹਾਡੀ ਭਾਸ਼ਾ ਵਿੱਚ.",  // Punjabi
  "ଆପଣଙ୍କ ଭାଷାରେ.",    // Odia
  "আপোনাৰ ভাষাত.",      // Assamese
  "آپ کی زبان میں۔",    // Urdu
  "In your language.",   // English
];

export default function LandingPage() {
  const t = useTranslations("landing");
  const tc = useTranslations("common");
  const [langIndex, setLangIndex] = useState(0);
  const [docIndex, setDocIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setLangIndex((prev) => (prev + 1) % LANG_CYCLE.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setDocIndex((prev) => (prev + 1) % DOC_KEYS.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* ─── Nav ─── */}
      <SiteHeader />

      {/* ═══════════════════════════════════════════
          SECTION 1 — HERO
      ═══════════════════════════════════════════ */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute left-1/2 top-0 -translate-x-1/2 h-[500px] w-[800px] rounded-full bg-brand/[0.03] blur-[100px]" />
        </div>
        <div className="relative mx-auto max-w-5xl px-4 pt-28 pb-24 text-center">
          <h1 className="text-5xl font-semibold tracking-tight sm:text-6xl lg:text-7xl leading-[1.1]">
            <span className="relative block h-[2.8em] sm:h-[1.3em] overflow-hidden">
              <span
                key={docIndex}
                className="absolute inset-x-0 top-0 flex items-center justify-center h-full animate-[cycleFadeIn_0.5s_ease-out_forwards]"
              >
                {t(DOC_KEYS[docIndex])}
              </span>
            </span>
            <span className="text-gradient">{t("heroTitleHighlight")}</span>
          </h1>
          <p className="mt-2 text-4xl font-semibold tracking-tight text-muted-foreground sm:text-5xl lg:text-6xl">
            {t("heroSubtitle")}
          </p>
          <p className="mx-auto mt-8 max-w-lg text-lg leading-relaxed text-muted-foreground">
            {t("heroDescription")}
          </p>
          <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
            <Link
              href="/signup"
              className={cn(
                buttonVariants({ size: "lg" }),
                "h-12 px-7 bg-brand text-brand-foreground hover:bg-brand/90 text-sm font-medium shadow-lg shadow-brand/20 rounded-full"
              )}
            >
              {t("heroCta")}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>
          <p className="mt-4 text-xs text-muted-foreground/50">
            {t("heroFreeOffer")}
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          SECTION 2 — LIVE DEMO with CSS Mockup
          Apple-style: show the product, not just describe it
      ═══════════════════════════════════════════ */}
      <section className="border-t border-border/30 py-16 bg-muted/20">
        <div className="mx-auto max-w-5xl px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-semibold tracking-tight sm:text-5xl">
              {t("demoTitle")}
              <br />
              <span className="text-muted-foreground">{t("demoSubtitle")}</span>
            </h2>
          </div>

          {/* Full-width CSS mockup of the scan results UI */}
          <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-2xl shadow-black/10">
            {/* App header bar mockup */}
            <div className="border-b border-border bg-muted/30 px-4 py-2.5 flex items-center gap-2">
              <div className="flex gap-1.5">
                <div className="h-2.5 w-2.5 rounded-full bg-red-400" />
                <div className="h-2.5 w-2.5 rounded-full bg-amber-400" />
                <div className="h-2.5 w-2.5 rounded-full bg-green-400" />
              </div>
              <div className="ml-4 flex-1 flex items-center justify-center">
                <div className="rounded-md bg-background/80 border border-border px-4 py-1 text-[10px] text-muted-foreground">
                  gotredflags.com/scan/results/demo
                </div>
              </div>
            </div>

            {/* Results UI mockup */}
            <div className="p-6 md:p-8">
              {/* Document header + risk gauge */}
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mb-8">
                <div className="flex items-center gap-5">
                  {/* Risk gauge */}
                  <div className="relative flex h-24 w-24 items-center justify-center shrink-0">
                    <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
                      <circle cx="50" cy="50" r="42" fill="none" stroke="currentColor" strokeWidth="6" className="text-border" />
                      <circle cx="50" cy="50" r="42" fill="none" stroke="currentColor" strokeWidth="6" className="text-amber-500" strokeDasharray={`${62 * 2.64} ${264 - 62 * 2.64}`} strokeLinecap="round" />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-2xl font-bold text-amber-500">62</span>
                      <span className="text-[9px] text-muted-foreground">/ 100</span>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                      <FileText size={16} className="text-muted-foreground" />
                      {t("demoDocName")}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1">Rental Agreement · 8 pages · Analyzed today</p>
                  </div>
                </div>
                {/* Summary badges */}
                <div className="flex gap-3">
                  <div className="flex items-center gap-1.5 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/30 px-3 py-2">
                    <div className="h-2 w-2 rounded-full bg-red-500" />
                    <span className="text-xs font-semibold text-red-600 dark:text-red-400">2 Red Flags</span>
                  </div>
                  <div className="flex items-center gap-1.5 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/30 px-3 py-2">
                    <div className="h-2 w-2 rounded-full bg-amber-500" />
                    <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">3 Warnings</span>
                  </div>
                  <div className="flex items-center gap-1.5 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900/30 px-3 py-2">
                    <div className="h-2 w-2 rounded-full bg-green-500" />
                    <span className="text-xs font-semibold text-green-600 dark:text-green-400">4 Safe</span>
                  </div>
                </div>
              </div>

              {/* Tab mockup */}
              <div className="flex gap-1 border-b border-border mb-6">
                {["Risk Analysis", "Compliance", "Checklist", "Summary"].map((tab, i) => (
                  <div
                    key={tab}
                    className={cn(
                      "px-4 py-2.5 text-xs font-medium transition-colors",
                      i === 0
                        ? "border-b-2 border-brand text-brand"
                        : "text-muted-foreground"
                    )}
                  >
                    {tab}
                  </div>
                ))}
              </div>

              {/* Clause findings */}
              <div className="space-y-3">
                {[
                  {
                    severity: "red" as const,
                    badge: t("demoFinding1Badge"),
                    title: t("demoFinding1Title"),
                    desc: t("demoFinding1Desc"),
                    law: "Model Tenancy Act, 2021 · Section 4(3)",
                  },
                  {
                    severity: "amber" as const,
                    badge: t("demoFinding2Badge"),
                    title: t("demoFinding2Title"),
                    desc: t("demoFinding2Desc"),
                    law: "Karnataka Rent Control Act · Section 9",
                  },
                  {
                    severity: "green" as const,
                    badge: t("demoFinding3Badge"),
                    title: t("demoFinding3Title"),
                    desc: t("demoFinding3Desc"),
                    law: "Model Tenancy Act, 2021 · Section 8",
                  },
                ].map((item, i) => (
                  <div
                    key={i}
                    className={cn(
                      "rounded-xl border p-5",
                      item.severity === "red"
                        ? "border-red-200 dark:border-red-900/30 bg-red-50/50 dark:bg-red-950/10"
                        : item.severity === "amber"
                          ? "border-amber-200 dark:border-amber-900/30 bg-amber-50/50 dark:bg-amber-950/10"
                          : "border-green-200 dark:border-green-900/30 bg-green-50/50 dark:bg-green-950/10"
                    )}
                  >
                    <div className="flex items-center gap-2.5 mb-2">
                      <div
                        className={cn(
                          "h-2.5 w-2.5 rounded-full",
                          item.severity === "red"
                            ? "bg-red-500"
                            : item.severity === "amber"
                              ? "bg-amber-500"
                              : "bg-green-500"
                        )}
                      />
                      <span
                        className={cn(
                          "text-[10px] font-bold uppercase tracking-wider",
                          item.severity === "red"
                            ? "text-red-600 dark:text-red-400"
                            : item.severity === "amber"
                              ? "text-amber-600 dark:text-amber-400"
                              : "text-green-600 dark:text-green-400"
                        )}
                      >
                        {item.badge}
                      </span>
                    </div>
                    <p className="text-sm font-semibold text-foreground">
                      {item.title}
                    </p>
                    <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
                      {item.desc}
                    </p>
                    <div className="mt-3 flex items-center gap-1.5">
                      <Scale size={10} className="text-brand" />
                      <span className="text-[10px] text-brand font-medium">{item.law}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          SECTION — LANGUAGE SHOWCASE
      ═══════════════════════════════════════════ */}
      <section className="border-t border-border/30 py-16">
        <div className="mx-auto max-w-5xl px-4 text-center">
          <h2 className="relative text-4xl font-semibold tracking-tight sm:text-5xl lg:text-6xl h-[1.6em] overflow-hidden">
            <span
              key={langIndex}
              className="absolute inset-x-0 top-0 flex items-center justify-center h-full animate-[cycleFadeIn_0.5s_ease-out_forwards]"
            >
              {LANG_CYCLE[langIndex]}
            </span>
          </h2>
          <p className="mt-1 text-4xl font-semibold tracking-tight text-muted-foreground sm:text-5xl lg:text-6xl">
            {t("langSubtitle")}
          </p>
          <p className="mx-auto mt-8 max-w-lg text-lg leading-relaxed text-muted-foreground">
            {t("langDescription")}
          </p>
          <div className="mt-14 flex flex-wrap items-center justify-center gap-3">
            {[
              "langHindi", "langTamil", "langTelugu", "langKannada",
              "langMalayalam", "langMarathi", "langBengali", "langGujarati",
              "langPunjabi", "langOdia", "langAssamese", "langUrdu", "langEnglish",
            ].map((key) => (
              <span
                key={key}
                className="rounded-full border border-border bg-card px-5 py-2.5 text-sm font-medium text-foreground/80 transition-colors hover:border-brand/40 hover:text-foreground"
              >
                {t(key)}
              </span>
            ))}
          </div>
          <p className="mt-6 text-xs text-muted-foreground">
            {t("langResultsIn")}
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          SECTION — PROPERTY DUE DILIGENCE (Apple-style)
      ═══════════════════════════════════════════ */}
      <section className="relative overflow-hidden py-28 sm:py-36">
        {/* Background glow effects */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute left-1/2 top-0 -translate-x-1/2 h-[600px] w-[900px] rounded-full bg-amber-500/[0.07] blur-[120px]" />
          <div className="absolute right-0 bottom-1/4 h-[400px] w-[600px] rounded-full bg-brand/[0.05] blur-[100px]" />
        </div>

        <div className="relative mx-auto max-w-5xl px-4">
          {/* Section label */}
          <div className="text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-amber-500/20 bg-amber-500/[0.08] px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-amber-500">
              <Building2 className="h-3.5 w-3.5" />
              {t("propertyDueDiligence")}
            </span>
          </div>

          {/* ── Feature 1: Auto-Fetch — hero-scale showcase ── */}
          <div className="mt-16 text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-amber-500/15 px-3 py-1 mb-6">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-500" />
              </span>
              <span className="text-xs font-bold uppercase tracking-wider text-amber-500">{t("autoFetchNew")}</span>
            </div>
            <h2 className="text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
              <span className="block text-foreground">{t("autoFetchHeadline1")}</span>
              <span className="bg-gradient-to-r from-amber-400 via-amber-500 to-orange-500 bg-clip-text text-transparent">
                {t("autoFetchHeadline2")}
              </span>
            </h2>
            <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground sm:text-xl">
              {t("autoFetchDesc")}
            </p>
          </div>

          {/* State badges — floating pill layout */}
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            {[
              { key: "autoFetchStateMaharashtra" },
              { key: "autoFetchStateKarnataka" },
              { key: "autoFetchStateTelangana" },
              { key: "autoFetchStateTamilNadu" },
            ].map(({ key }) => (
              <span
                key={key}
                className="group inline-flex items-center gap-2 rounded-full border border-amber-500/25 bg-amber-500/[0.08] px-5 py-2.5 text-sm font-medium text-foreground backdrop-blur-sm transition-all hover:border-amber-500/50 hover:bg-amber-500/[0.15]"
              >
                <Check className="h-4 w-4 text-amber-500" />
                {t(key)}
              </span>
            ))}
            <span className="inline-flex items-center gap-2 rounded-full border border-border/50 px-5 py-2.5 text-sm text-muted-foreground">
              <Sparkles className="h-4 w-4" />
              {t("autoFetchComingSoon")}
            </span>
          </div>

          {/* CTA */}
          <div className="mt-10 flex justify-center">
            <Link
              href="/signup"
              className="group inline-flex h-14 items-center gap-3 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 px-8 text-base font-semibold text-black shadow-lg shadow-amber-500/25 transition-all hover:shadow-xl hover:shadow-amber-500/30 hover:scale-[1.02] active:scale-[0.98]"
            >
              {t("autoFetchCta")}
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>

          {/* ── Visual separator ── */}
          <div className="my-24 flex items-center justify-center gap-4">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-border to-transparent" />
            <div className="flex h-10 w-10 items-center justify-center rounded-full border border-border/50 bg-card">
              <Zap className="h-4 w-4 text-amber-500" />
            </div>
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-border to-transparent" />
          </div>

          {/* ── Feature 2: Bulk 500-page scan — dramatic statement ── */}
          <div className="text-center">
            <h2 className="text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
              <span className="text-foreground">{t("bulkScanTitle")}</span>
              {" "}
              <span className="bg-gradient-to-r from-brand via-emerald-400 to-teal-400 bg-clip-text text-transparent">{t("bulkScanHighlight")}</span>
            </h2>
            <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground sm:text-xl">
              {t("bulkScanDesc")}
            </p>
            <p className="mt-4 text-base font-medium text-foreground/80">
              {t("propertyPricing")}
            </p>
          </div>

          {/* ── Capability cards — 2×2 bento grid ── */}
          <div className="mt-16 grid gap-4 sm:grid-cols-2">
            {[
              { icon: Search, title: t("propertyCard1Title"), desc: t("propertyCard1Desc"), accent: "amber" },
              { icon: FileWarning, title: t("propertyCard2Title"), desc: t("propertyCard2Desc"), accent: "red" },
              { icon: ClipboardList, title: t("propertyCard3Title"), desc: t("propertyCard3Desc"), accent: "blue" },
              { icon: ShieldCheck, title: t("propertyCard4Title"), desc: t("propertyCard4Desc"), accent: "emerald" },
            ].map((item, i) => (
              <div
                key={i}
                className="group relative overflow-hidden rounded-3xl border border-border/50 bg-card/50 p-8 backdrop-blur-sm transition-all duration-300 hover:border-amber-500/30 hover:bg-card/80"
              >
                <div className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-amber-500/[0.04] blur-2xl transition-all duration-500 group-hover:bg-amber-500/[0.08]" />
                <div className={cn(
                  "mb-5 flex h-12 w-12 items-center justify-center rounded-2xl",
                  i === 0 && "bg-amber-500/10 text-amber-500",
                  i === 1 && "bg-red-500/10 text-red-500",
                  i === 2 && "bg-blue-500/10 text-blue-500",
                  i === 3 && "bg-emerald-500/10 text-emerald-500",
                )}>
                  <item.icon className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-semibold tracking-tight">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="mt-12 text-center">
            <Link
              href="/signup"
              className="group inline-flex items-center gap-2 text-base font-medium text-amber-500 transition-colors hover:text-amber-400"
            >
              {t("startPropertyCheck")}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          SECTION 3 — FOUR REPORTS (Feature Showcase)
          Each report type gets visual treatment
      ═══════════════════════════════════════════ */}
      <section className="border-t border-border/30 py-16">
        <div className="mx-auto max-w-5xl px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-semibold tracking-tight sm:text-5xl">
              {t("reportsTitle")}
              <br />
              <span className="text-muted-foreground">{t("reportsTitleHighlight")}</span>
            </h2>
            <p className="mx-auto mt-6 max-w-lg text-lg leading-relaxed text-muted-foreground">
              {t("reportsDescription")}
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            {/* Risk Analysis card with mini mockup */}
            <div className="rounded-2xl border border-border bg-card p-6 transition-all hover:border-brand/30 overflow-hidden">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-red-500/10">
                  <AlertTriangle className="h-6 w-6 text-red-500" />
                </div>
                <div>
                  <h3 className="text-base font-semibold">{t("reportRiskTitle")}</h3>
                </div>
              </div>
              <p className="text-sm leading-relaxed text-muted-foreground mb-5">
                {t("reportRiskDesc")}
              </p>
              {/* Mini preview */}
              <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-2">
                {[
                  { color: "bg-red-500", w: "w-full", label: "Lock-in without exit" },
                  { color: "bg-amber-500", w: "w-4/5", label: "Uncapped rent increase" },
                  { color: "bg-green-500", w: "w-3/5", label: "Standard deposit" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className={cn("h-2 w-2 rounded-full shrink-0", item.color)} />
                    <div className="flex-1">
                      <div className={cn("h-1.5 rounded-full", item.color, "opacity-20", item.w)} />
                    </div>
                    <span className="text-[9px] text-muted-foreground shrink-0">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Compliance Report card */}
            <div className="rounded-2xl border border-border bg-card p-6 transition-all hover:border-brand/30 overflow-hidden">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-500/10">
                  <Scale className="h-6 w-6 text-blue-500" />
                </div>
                <div>
                  <h3 className="text-base font-semibold">{t("reportComplianceTitle")}</h3>
                </div>
              </div>
              <p className="text-sm leading-relaxed text-muted-foreground mb-5">
                {t("reportComplianceDesc")}
              </p>
              {/* Mini compliance preview */}
              <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-2.5">
                {[
                  { status: "pass", law: "Model Tenancy Act" },
                  { status: "fail", law: "Karnataka Rent Control" },
                  { status: "pass", law: "Registration Act" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between text-[10px]">
                    <div className="flex items-center gap-2">
                      <Scale size={10} className="text-muted-foreground" />
                      <span className="text-foreground/70">{item.law}</span>
                    </div>
                    <span className={cn(
                      "font-semibold",
                      item.status === "pass" ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
                    )}>
                      {item.status === "pass" ? "Compliant" : "Violation"}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Checklist card */}
            <div className="rounded-2xl border border-border bg-card p-6 transition-all hover:border-brand/30 overflow-hidden">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand/10">
                  <ListChecks className="h-6 w-6 text-brand" />
                </div>
                <div>
                  <h3 className="text-base font-semibold">{t("reportChecklistTitle")}</h3>
                </div>
              </div>
              <p className="text-sm leading-relaxed text-muted-foreground mb-5">
                {t("reportChecklistDesc")}
              </p>
              {/* Mini checklist preview */}
              <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-2.5">
                {[
                  { checked: true, text: "Negotiate exit clause", priority: "Critical" },
                  { checked: true, text: "Cap rent escalation", priority: "Critical" },
                  { checked: false, text: "Get maintenance breakdown", priority: "Recommended" },
                  { checked: false, text: "Verify landlord identity", priority: "Optional" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <div className={cn(
                      "h-4 w-4 rounded border flex items-center justify-center shrink-0",
                      item.checked ? "bg-brand border-brand" : "border-border"
                    )}>
                      {item.checked && <Check size={10} className="text-brand-foreground" />}
                    </div>
                    <span className={cn("text-[10px] flex-1", item.checked ? "text-muted-foreground line-through" : "text-foreground/80")}>
                      {item.text}
                    </span>
                    <span className={cn(
                      "text-[8px] font-semibold uppercase px-1.5 py-0.5 rounded",
                      item.priority === "Critical" ? "bg-red-100 dark:bg-red-950/30 text-red-600 dark:text-red-400" :
                      item.priority === "Recommended" ? "bg-amber-100 dark:bg-amber-950/30 text-amber-600 dark:text-amber-400" :
                      "bg-muted text-muted-foreground"
                    )}>
                      {item.priority}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Summary Report card */}
            <div className="rounded-2xl border border-border bg-card p-6 transition-all hover:border-brand/30 overflow-hidden">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-purple-500/10">
                  <BarChart3 className="h-6 w-6 text-purple-500" />
                </div>
                <div>
                  <h3 className="text-base font-semibold">{t("reportSummaryTitle")}</h3>
                </div>
              </div>
              <p className="text-sm leading-relaxed text-muted-foreground mb-5">
                {t("reportSummaryDesc")}
              </p>
              {/* Mini summary preview */}
              <div className="rounded-xl border border-border bg-muted/30 p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] font-semibold text-foreground/70">Risk Score</span>
                  <span className="text-lg font-bold text-amber-500">62<span className="text-[10px] text-muted-foreground font-normal">/100</span></span>
                </div>
                <div className="h-2 rounded-full bg-border overflow-hidden mb-3">
                  <div className="h-full w-[62%] rounded-full bg-gradient-to-r from-amber-400 to-amber-500" />
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-lg bg-red-50 dark:bg-red-950/20 py-1.5">
                    <p className="text-sm font-bold text-red-500">2</p>
                    <p className="text-[8px] text-muted-foreground">Red Flags</p>
                  </div>
                  <div className="rounded-lg bg-amber-50 dark:bg-amber-950/20 py-1.5">
                    <p className="text-sm font-bold text-amber-500">3</p>
                    <p className="text-[8px] text-muted-foreground">Warnings</p>
                  </div>
                  <div className="rounded-lg bg-green-50 dark:bg-green-950/20 py-1.5">
                    <p className="text-sm font-bold text-green-500">4</p>
                    <p className="text-[8px] text-muted-foreground">Safe</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          SECTION 4 — ASK AI (with chat mockup)
      ═══════════════════════════════════════════ */}
      <section className="border-t border-border/30 py-16 bg-muted/20">
        <div className="mx-auto max-w-5xl px-4">
          <div className="grid gap-12 md:grid-cols-2 items-center">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.15em] text-brand">
                AI Assistant
              </p>
              <h2 className="mt-4 text-4xl font-semibold tracking-tight sm:text-5xl leading-[1.1]">
                {t("askAiTitle")}
                <br />
                <span className="text-gradient">{t("askAiTitleHighlight")}</span>
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
                {t("askAiDescription")}
              </p>
              <Link
                href="/try"
                className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-brand hover:underline"
              >
                Try it free — no signup needed
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>

            {/* Chat mockup */}
            <div className="rounded-2xl border border-border bg-card shadow-xl shadow-black/5 overflow-hidden">
              <div className="border-b border-border px-5 py-3 flex items-center gap-2">
                <Sparkles size={14} className="text-brand" />
                <span className="text-xs font-semibold text-foreground">Ask AI about your document</span>
              </div>
              <div className="p-5 space-y-4 min-h-[300px]">
                {/* User message */}
                <div className="flex justify-end">
                  <div className="rounded-2xl rounded-tr-sm bg-brand text-brand-foreground px-4 py-2.5 max-w-[80%]">
                    <p className="text-xs">{t("askAiChat1Q")}</p>
                  </div>
                </div>
                {/* AI response */}
                <div className="flex justify-start">
                  <div className="rounded-2xl rounded-tl-sm bg-muted border border-border px-4 py-2.5 max-w-[85%]">
                    <p className="text-xs leading-relaxed text-foreground/80">{t("askAiChat1A")}</p>
                    <div className="mt-2 flex items-center gap-1.5">
                      <Scale size={9} className="text-brand" />
                      <span className="text-[9px] text-brand font-medium">Model Tenancy Act, 2021</span>
                    </div>
                  </div>
                </div>
                {/* User message 2 */}
                <div className="flex justify-end">
                  <div className="rounded-2xl rounded-tr-sm bg-brand text-brand-foreground px-4 py-2.5 max-w-[80%]">
                    <p className="text-xs">{t("askAiChat2Q")}</p>
                  </div>
                </div>
                {/* AI response 2 */}
                <div className="flex justify-start">
                  <div className="rounded-2xl rounded-tl-sm bg-muted border border-border px-4 py-2.5 max-w-[85%]">
                    <p className="text-xs leading-relaxed text-foreground/80">{t("askAiChat2A")}</p>
                  </div>
                </div>
              </div>
              {/* Chat input */}
              <div className="border-t border-border px-4 py-3 flex items-center gap-2">
                <div className="flex-1 rounded-lg bg-muted/50 border border-border px-3 py-2">
                  <span className="text-[10px] text-muted-foreground">{t("askAiPlaceholder")}</span>
                </div>
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand">
                  <Send size={12} className="text-brand-foreground" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          SECTION 5 — CONTRACT SCANNER
      ═══════════════════════════════════════════ */}
      <section className="border-t border-border/30 py-16">
        <div className="mx-auto max-w-5xl px-4">
          <div className="max-w-2xl">
            <p className="text-xs font-medium uppercase tracking-[0.15em] text-brand">
              Contract Scanner
            </p>
            <h2 className="mt-4 text-4xl font-semibold tracking-tight sm:text-5xl leading-[1.1]">
              {t("contractTitle")}
              <br />
              <span className="text-muted-foreground">
                {t("contractTitleHighlight")}
              </span>
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
              {t("contractDescription")}
            </p>
            <p className="mt-4 text-sm font-medium text-foreground/70">
              {t("contractPricing")}
            </p>
          </div>

          {/* Document type cards */}
          <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { icon: Home, title: t("contractFeatureRental"), desc: t("contractFeatureRentalDesc") },
              { icon: Briefcase, title: t("contractFeatureEmployment"), desc: t("contractFeatureEmploymentDesc") },
              { icon: Landmark, title: t("contractFeatureLoan"), desc: t("contractFeatureLoanDesc") },
              { icon: Shield, title: t("contractFeatureNda"), desc: t("contractFeatureNdaDesc") },
              { icon: Handshake, title: t("contractFeaturePartnership"), desc: t("contractFeaturePartnershipDesc") },
              { icon: TrendingUp, title: t("contractFeatureStartup"), desc: t("contractFeatureStartupDesc") },
            ].map((item, i) => (
              <div
                key={i}
                className="rounded-2xl border border-border bg-card p-6 transition-all hover:border-brand/30"
              >
                <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-brand/10">
                  <item.icon className="h-5 w-5 text-brand" />
                </div>
                <h3 className="text-sm font-semibold">{item.title}</h3>
                <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs text-muted-foreground">
            {t("contractFeatureMore")}
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          SECTION 7 — TEMPLATES (with visual preview)
      ═══════════════════════════════════════════ */}
      <section className="border-t border-border/30 py-16 bg-muted/20">
        <div className="mx-auto max-w-5xl px-4">
          <div className="grid gap-12 md:grid-cols-2 items-center">
            {/* Template visual mockup */}
            <div className="rounded-2xl border border-border bg-card shadow-xl shadow-black/5 overflow-hidden order-2 md:order-1">
              <div className="border-b border-border px-5 py-3 flex items-center gap-2">
                <BookOpen size={14} className="text-brand" />
                <span className="text-xs font-semibold text-foreground">Document Templates</span>
              </div>
              <div className="p-5">
                {/* Template grid mockup */}
                <div className="grid grid-cols-3 gap-2.5">
                  {[
                    { icon: Home, label: "Rental", color: "text-brand" },
                    { icon: Briefcase, label: "Employment", color: "text-blue-500" },
                    { icon: Landmark, label: "Loan", color: "text-amber-500" },
                    { icon: Shield, label: "NDA", color: "text-purple-500" },
                    { icon: Handshake, label: "Partnership", color: "text-green-500" },
                    { icon: TrendingUp, label: "Startup", color: "text-red-500" },
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="rounded-xl border border-border bg-muted/30 p-3 text-center transition-all hover:border-brand/30"
                    >
                      <item.icon className={cn("h-5 w-5 mx-auto mb-1.5", item.color)} />
                      <span className="text-[9px] font-medium text-foreground/70">{item.label}</span>
                    </div>
                  ))}
                </div>
                {/* Template detail mockup */}
                <div className="mt-4 rounded-xl border border-border bg-background p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Home size={12} className="text-brand" />
                    <span className="text-xs font-semibold">Rental Agreement</span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-1.5 rounded-full bg-brand" />
                      <span className="text-[9px] text-foreground/60">Must-have: Lock-in exit clause</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-1.5 rounded-full bg-brand" />
                      <span className="text-[9px] text-foreground/60">Must-have: Rent escalation cap</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-1.5 rounded-full bg-brand" />
                      <span className="text-[9px] text-foreground/60">Must-have: Security deposit refund</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 size={10} className="text-green-500" />
                      <span className="text-[9px] text-green-600 dark:text-green-400 font-medium">Sample clause language included</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="order-1 md:order-2">
              <p className="text-xs font-medium uppercase tracking-[0.15em] text-brand">
                Templates
              </p>
              <h2 className="mt-4 text-4xl font-semibold tracking-tight sm:text-5xl leading-[1.1]">
                {t("templatesTitle")}
                <br />
                <span className="text-gradient">{t("templatesTitleHighlight")}</span>
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
                {t("templatesDescription")}
              </p>
              {/* Stats */}
              <div className="mt-8 flex gap-8">
                <div>
                  <p className="text-3xl font-bold text-foreground">{t("templatesCount")}</p>
                  <p className="text-xs text-muted-foreground mt-1">{t("templatesCountLabel")}</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-foreground">{t("templatesClauses")}</p>
                  <p className="text-xs text-muted-foreground mt-1">{t("templatesClausesLabel")}</p>
                </div>
              </div>
              <p className="mt-6 text-xs text-muted-foreground">
                {t("templatesTypes")}
              </p>
              <Link
                href="/templates"
                className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-brand hover:underline"
              >
                Browse templates
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          SECTION 8 — HOW IT WORKS
      ═══════════════════════════════════════════ */}
      <section className="border-t border-border/30 py-16">
        <div className="mx-auto max-w-5xl px-4">
          <h2 className="text-center text-4xl font-semibold tracking-tight sm:text-5xl">
            {t("howTitle")}
          </h2>
          <div className="mt-16 grid gap-6 md:grid-cols-3">
            {[
              { icon: Upload, title: t("howStep1Title"), desc: t("howStep1Desc") },
              { icon: Brain, title: t("howStep2Title"), desc: t("howStep2Desc") },
              { icon: FileCheck, title: t("howStep3Title"), desc: t("howStep3Desc") },
            ].map((step, i) => (
              <div
                key={i}
                className="rounded-2xl border border-border bg-card p-8 text-center transition-all hover:border-brand/30"
              >
                <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-3xl bg-brand/10">
                  <step.icon className="h-9 w-9 text-brand" />
                </div>
                <h3 className="text-base font-semibold tracking-tight">
                  {step.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {step.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          SECTION 10 — BIG NUMBERS
      ═══════════════════════════════════════════ */}
      <section className="border-t border-border/30 py-16 bg-muted/20">
        <div className="mx-auto max-w-5xl px-4">
          <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
            {[
              { value: t("numbersLaws"), label: t("numbersLawsLabel") },
              { value: t("numbersDocTypes"), label: t("numbersDocTypesLabel") },
              { value: t("numbersPrice"), label: t("numbersPriceLabel") },
              { value: t("numbersMinutes"), label: t("numbersMinutesLabel") },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <p className="text-5xl font-bold tracking-tight text-foreground sm:text-6xl lg:text-7xl">
                  {stat.value}
                </p>
                <p className="mt-3 text-sm text-muted-foreground">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          SECTION 11 — BUILT ON INDIAN LAW
      ═══════════════════════════════════════════ */}
      <section className="border-t border-border/30 py-16">
        <div className="mx-auto max-w-5xl px-4 text-center">
          <h2 className="text-4xl font-semibold tracking-tight sm:text-5xl leading-[1.1]">
            {t("indiaTitle")}
            <br />
            <span className="text-gradient">{t("indiaTitleHighlight")}</span>
          </h2>
          <p className="mx-auto mt-6 max-w-lg text-lg leading-relaxed text-muted-foreground">
            {t("indiaDescription")}
          </p>

          {/* Law cards in a grid */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { key: "indiaLaw1", icon: ScrollText },
              { key: "indiaLaw2", icon: Building2 },
              { key: "indiaLaw3", icon: TrendingUp },
              { key: "indiaLaw4", icon: CreditCard },
              { key: "indiaLaw5", icon: Globe },
              { key: "indiaLaw6", icon: Handshake },
              { key: "indiaLaw7", icon: Home },
              { key: "indiaLaw8", icon: Briefcase },
            ].map((item) => (
              <div
                key={item.key}
                className="rounded-xl border border-border bg-card p-4 text-center transition-all hover:border-brand/30"
              >
                <div className="mx-auto mb-2.5 flex h-9 w-9 items-center justify-center rounded-lg bg-brand/10">
                  <item.icon className="h-4 w-4 text-brand" />
                </div>
                <span className="text-xs font-medium text-foreground/80 leading-tight">
                  {t(item.key)}
                </span>
              </div>
            ))}
          </div>
          <p className="mt-6 text-sm text-muted-foreground flex items-center justify-center gap-1.5">
            <Globe className="h-3.5 w-3.5" />
            {t("indiaStates")}
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          SECTION 12 — FAQ
      ═══════════════════════════════════════════ */}
      <section className="border-t border-border/30 py-16">
        <div className="mx-auto max-w-2xl px-4">
          <h2 className="text-center text-4xl font-semibold tracking-tight sm:text-5xl">
            {t("faqTitle")}{" "}
            <span className="text-muted-foreground">{t("faqTitleHighlight")}</span>
          </h2>
          <div className="mt-14 space-y-0 divide-y divide-border rounded-2xl border border-border overflow-hidden">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <details key={i} className="group bg-card">
                <summary className="flex cursor-pointer items-center justify-between px-6 py-5 text-sm font-medium text-foreground [&::-webkit-details-marker]:hidden">
                  {t(`faqQ${i}`)}
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 group-open:rotate-90" />
                </summary>
                <div className="px-6 pb-5 -mt-1 text-sm leading-relaxed text-muted-foreground">
                  {t(`faqA${i}`)}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          SECTION 13 — FINAL CTA
      ═══════════════════════════════════════════ */}
      <section className="border-t border-border/30 py-16">
        <div className="relative mx-auto max-w-2xl px-4 text-center">
          <div className="pointer-events-none absolute inset-0 -z-10 flex items-center justify-center">
            <div className="h-[300px] w-[500px] rounded-full bg-brand/[0.04] blur-[100px]" />
          </div>
          <h2 className="text-5xl font-semibold tracking-tight sm:text-6xl">
            {t("ctaTitle")}
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            {t("ctaDescription")}
          </p>
          <div className="mt-10">
            <Link
              href="/signup"
              className={cn(
                buttonVariants({ size: "lg" }),
                "h-12 px-8 bg-brand text-brand-foreground hover:bg-brand/90 text-sm font-medium shadow-lg shadow-brand/20 rounded-full"
              )}
            >
              {t("ctaButton")}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ─── Footer ─── */}
      <footer className="border-t border-border/30 py-8">
        <div className="mx-auto max-w-6xl px-4">
          <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
            <div className="flex items-center gap-2">
              <div className="flex h-5 w-5 items-center justify-center rounded-md bg-brand">
                <ScanLine className="h-2.5 w-2.5 text-brand-foreground" />
              </div>
              <span className="text-xs font-semibold">{tc("appName")}</span>
            </div>
            <div className="flex items-center gap-5 text-xs text-muted-foreground">
              <Link href="/privacy" className="transition-colors hover:text-foreground">
                {t("footer.privacy")}
              </Link>
              <Link href="/terms" className="transition-colors hover:text-foreground">
                {t("footer.terms")}
              </Link>
              <Link href="/tools" className="transition-colors hover:text-foreground">
                {t("navTools")}
              </Link>
              <Link href="/pricing" className="transition-colors hover:text-foreground">
                {t("footerPricing")}
              </Link>
              <Link href="/blog" className="transition-colors hover:text-foreground">
                {t("footerBlog")}
              </Link>
              <a href="mailto:hello@gotredflags.com" className="transition-colors hover:text-foreground">
                {t("footer.contact")}
              </a>
            </div>
          </div>
          <p className="mt-6 text-center text-[10px] leading-relaxed text-muted-foreground/40">
            {t("footer.disclaimer")}
          </p>
        </div>
      </footer>
    </div>
  );
}
